import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { getStoredUserId } from '../../utils/auth';
import { API_BASE_URL } from '../../constants/apiConstants';
import './LinkedInAnalytics.css';

// ─── Session-level posts cache (survives React re-mounts within the same browser tab) ─
const _postsCache = {}; // { [orgId]: Post[] }

// ─── Colour palette ───────────────────────────────────────────────────────────
const PIE_COLORS = [
  '#0a66c2', '#00b0ff', '#43a047', '#fb8c00', '#8e24aa',
  '#e53935', '#00897b', '#f4511e', '#6d4c41', '#546e7a'
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtNum = (n) => {
  if (!n && n !== 0) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(n);
};

const fmtDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ─── Reusable sub-components ──────────────────────────────────────────────────
const MetricCard = ({ icon, label, value, sub, subPositive }) => (
  <div className="li-metric-card">
    <div className="li-metric-icon">{icon}</div>
    <div className="li-metric-body">
      <div className="li-metric-value">{value}</div>
      <div className="li-metric-label">{label}</div>
      {sub != null && (
        <div className={`li-metric-sub ${subPositive === true ? 'positive' : subPositive === false ? 'negative' : ''}`}>
          {sub}
        </div>
      )}
    </div>
  </div>
);

const SectionCard = ({ title, children, action }) => (
  <div className="li-section-card">
    <div className="li-section-header">
      <h3>{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const EmptyChart = ({ msg }) => (
  <div className="li-empty-chart">{msg || 'No data available for this period'}</div>
);

const CollapsibleSection = ({ title, defaultOpen = true, children, badge }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="li-collapsible">
      <button className="li-collapsible-header" onClick={() => setOpen(o => !o)}>
        <span className="li-collapsible-title">{title}</span>
        {badge != null && <span className="li-collapsible-badge">{badge}</span>}
        <span className={`li-collapsible-chevron ${open ? 'open' : ''}`}>▾</span>
      </button>
      {open && <div className="li-collapsible-body">{children}</div>}
    </div>
  );
};

const Spinner = ({ label }) => (
  <div className="li-spinner-wrap">
    <div className="li-spinner" />
    {label && <p>{label}</p>}
  </div>
);

const DemoPieChart = ({ data, nameKey, valueKey, title }) => {
  if (!data || data.length === 0) return <EmptyChart />;
  const top = data.slice(0, 8);
  return (
    <div className="li-pie-wrap">
      {title && <p className="li-pie-title">{title}</p>}
      <ResponsiveContainer width="100%" height={220}>
        <PieChart>
          <Pie data={top} dataKey={valueKey} nameKey={nameKey}
            cx="50%" cy="50%" outerRadius={80}
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
            labelLine={false}>
            {top.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
          </Pie>
          <Tooltip formatter={(v) => fmtNum(v)} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const LinkedInAnalytics = () => {
  // ── Connection / org state ──────────────────────────────────────────────────
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);

  // ── Tab / date range ────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('overview');
  const [dayRange, setDayRange] = useState(30);

  // ── Overview / engagement ───────────────────────────────────────────────────
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [engagementTimeline, setEngagementTimeline] = useState([]);

  // ── Follower analytics ──────────────────────────────────────────────────────
  const [followerGains, setFollowerGains] = useState(null);
  const [followerDemographics, setFollowerDemographics] = useState(null);
  const [followersLoading, setFollowersLoading] = useState(false);

  // ── Page view analytics ─────────────────────────────────────────────────────
  const [pageStats, setPageStats] = useState(null);
  const [pageTimeline, setPageTimeline] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);

  // ── Posts ───────────────────────────────────────────────────────────────────
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // Track which orgId's posts are already loaded so we don't re-fetch on tab switch
  const postsLoadedForOrg = useRef(null);

  // ── Post modal ──────────────────────────────────────────────────────────────
  const [showPostModal, setShowPostModal] = useState(false);
  const [postData, setPostData] = useState({ caption: '', imageUrl: '' });
  const [posting, setPosting] = useState(false);

  // ── Token revocation ────────────────────────────────────────────────────────
  const [requiresReconnect, setRequiresReconnect] = useState(false);
  const [pagePermissionDenied, setPagePermissionDenied] = useState(false);

  // ── Init ────────────────────────────────────────────────────────────────────
  useEffect(() => { checkConnection(); }, []);

  // When org changes, clear the posts cache so the new org fetches fresh data
  useEffect(() => {
    if (!selectedOrg?.organization_id) return;
    postsLoadedForOrg.current = null;
    loadTabData(activeTab, selectedOrg.organization_id, dayRange);
  }, [selectedOrg?.organization_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // When tab or day range changes, reload data
  useEffect(() => {
    if (!selectedOrg?.organization_id) return;
    loadTabData(activeTab, selectedOrg.organization_id, dayRange);
  }, [activeTab, dayRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Data loading orchestrator ───────────────────────────────────────────────
  const loadTabData = useCallback(async (tab, orgId, days) => {
    switch (tab) {
      case 'overview':
        await Promise.all([fetchOverviewAnalytics(orgId, days), fetchPosts(orgId)]);
        break;
      case 'followers':
        await fetchFollowerAnalytics(orgId, days);
        break;
      case 'pageviews':
        await fetchPageAnalytics(orgId, days);
        break;
      case 'posts':
        await fetchPosts(orgId);
        break;
      default:
        break;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Connection check ────────────────────────────────────────────────────────
  const checkConnection = async () => {
    try {
      setLoading(true);
      const userid = getStoredUserId();
      if (!userid) { setLoading(false); return; }

      const res = await axios.get(`${API_BASE_URL}/linkedin/auth/status`, {
        headers: { userid }
      });
      setConnectionStatus(res.data);
      if (res.data.connected) await fetchOrganizations();
    } catch {
      setConnectionStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    const userid = getStoredUserId();
    const res = await axios.get(`${API_BASE_URL}/linkedin/organizations`, {
      headers: { userid }
    });
    if (res.data.success && res.data.organizations?.length) {
      setOrganizations(res.data.organizations);
      const primary = res.data.organizations.find(o => o.is_primary) || res.data.organizations[0];
      setSelectedOrg(primary);
    }
  };

  // ── Overview analytics ──────────────────────────────────────────────────────
  const fetchOverviewAnalytics = async (orgId, days) => {
    try {
      setAnalyticsLoading(true);
      const userid = getStoredUserId();

      // Sync first (best-effort)
      try {
        await axios.post(`${API_BASE_URL}/linkedin/analytics/sync`,
          { organization_id: orgId, days },
          { headers: { userid } }
        );
      } catch { /* non-blocking */ }

      const [summaryRes, followerRes, detailRes] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/linkedin/analytics/${orgId}?days=${days}`, { headers: { userid } }),
        axios.get(`${API_BASE_URL}/linkedin/analytics/${orgId}/followers?days=${days}`, { headers: { userid } }),
        axios.get(`${API_BASE_URL}/linkedin/analytics/${orgId}?days=${days}`, { headers: { userid } })
      ]);

      // Detect revoked token from any settled result
      const revokedInOverview = [summaryRes, followerRes, detailRes].find(
        r => r.status === 'rejected' && r.reason?.response?.data?.requiresReconnect
      );
      if (revokedInOverview) { setRequiresReconnect(true); return; }

      const summary = summaryRes.status === 'fulfilled' ? summaryRes.value.data?.summary || {} : {};
      const followerSummary = followerRes.status === 'fulfilled' ? followerRes.value.data?.summary || {} : {};

      setAnalytics({ ...summary, ...followerSummary });

      // Build engagement timeline from detailed analytics
      if (detailRes.status === 'fulfilled') {
        const rows = detailRes.value.data?.analytics || [];
        const timeline = [...rows].reverse().map(r => ({
          date: fmtDate(r.date),
          Impressions: r.impressions || 0,
          Clicks: r.clicks || 0,
          Likes: r.likes || 0,
          Comments: r.comments || 0,
          Shares: r.shares || 0,
          Reach: r.reach || 0
        }));
        setEngagementTimeline(timeline);
      }
    } catch (err) {
      if (err.response?.data?.requiresReconnect) { setRequiresReconnect(true); return; }
      console.error('Error fetching overview analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // ── Follower analytics ──────────────────────────────────────────────────────
  const fetchFollowerAnalytics = async (orgId, days) => {
    try {
      setFollowersLoading(true);
      const userid = getStoredUserId();

      const [gainsRes, demoRes] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/linkedin/analytics/${orgId}/followers/gains?days=${days}`, { headers: { userid } }),
        axios.get(`${API_BASE_URL}/linkedin/analytics/${orgId}/followers/demographics`, { headers: { userid } })
      ]);

      const revokedInFollowers = [gainsRes, demoRes].find(
        r => r.status === 'rejected' && r.reason?.response?.data?.requiresReconnect
      );
      if (revokedInFollowers) { setRequiresReconnect(true); return; }

      if (gainsRes.status === 'fulfilled' && gainsRes.value.data?.success) {
        setFollowerGains(gainsRes.value.data);
      }
      if (demoRes.status === 'fulfilled' && demoRes.value.data?.success) {
        setFollowerDemographics(demoRes.value.data.data);
      }
    } catch (err) {
      if (err.response?.data?.requiresReconnect) { setRequiresReconnect(true); return; }
      console.error('Error fetching follower analytics:', err);
    } finally {
      setFollowersLoading(false);
    }
  };

  // ── Page view analytics ─────────────────────────────────────────────────────
  const fetchPageAnalytics = async (orgId, days) => {
    try {
      setPageLoading(true);
      setPagePermissionDenied(false);
      const userid = getStoredUserId();

      const [lifetimeRes, timeBoundRes] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/linkedin/analytics/${orgId}/page-stats`, { headers: { userid } }),
        axios.get(`${API_BASE_URL}/linkedin/analytics/${orgId}/page-stats/time-bound?days=${days}`, { headers: { userid } })
      ]);

      // Detect revoked token or scope errors from any rejected result
      const authError = [lifetimeRes, timeBoundRes].find(
        r => r.status === 'rejected' && r.reason?.response?.data?.requiresReconnect
      );
      if (authError) {
        const errData = authError.reason?.response?.data;
        if (errData?.error === 'INSUFFICIENT_SCOPE') {
          setPagePermissionDenied(true);
        } else {
          setRequiresReconnect(true);
        }
        return;
      }

      if (lifetimeRes.status === 'fulfilled' && lifetimeRes.value.data?.success) {
        setPageStats(lifetimeRes.value.data.data);
      }
      if (timeBoundRes.status === 'fulfilled' && timeBoundRes.value.data?.success) {
        const rows = timeBoundRes.value.data.data || [];
        setPageTimeline(rows.map(r => ({
          date: fmtDate(r.date),
          'All Views':     r.allPageViews        || 0,
          'Overview':      r.overviewPageViews    || 0,
          'Careers':       r.careersPageViews     || 0,
          'Jobs':          r.jobsPageViews        || 0,
          'Life At':       r.lifeAtPageViews      || 0,
          'Mobile':        r.allMobilePageViews   || 0,
          'Desktop':       r.allDesktopPageViews  || 0
        })));
      }
    } catch (err) {
      if (err.response?.data?.error === 'INSUFFICIENT_SCOPE') { setPagePermissionDenied(true); return; }
      if (err.response?.data?.requiresReconnect) { setRequiresReconnect(true); return; }
      console.error('Error fetching page analytics:', err);
    } finally {
      setPageLoading(false);
    }
  };

  // ── Posts ───────────────────────────────────────────────────────────────────
  const fetchPosts = async (orgId, force = false) => {
    // Use module-level cache so posts survive page navigation and re-mounts
    if (!force && _postsCache[orgId]) {
      setPosts(_postsCache[orgId]);
      postsLoadedForOrg.current = orgId;
      return;
    }
    try {
      setPostsLoading(true);
      const userid = getStoredUserId();
      let res;
      try {
        res = await axios.get(`${API_BASE_URL}/linkedin/posts/${orgId}/refresh`, { headers: { userid } });
      } catch {
        res = await axios.get(`${API_BASE_URL}/linkedin/posts/${orgId}`, { headers: { userid } });
      }
      const fetched = res.data?.success && Array.isArray(res.data.data) ? res.data.data : [];
      _postsCache[orgId] = fetched;
      setPosts(fetched);
      postsLoadedForOrg.current = orgId;
    } catch {
      setPosts([]);
    } finally {
      setPostsLoading(false);
    }
  };

  const syncPosts = async (orgId) => {
    try {
      setSyncing(true);
      const userid = getStoredUserId();
      try {
        await axios.post(`${API_BASE_URL}/linkedin/posts/${orgId}/sync`, {}, { headers: { userid } });
      } catch { /* non-blocking */ }
      delete _postsCache[orgId]; // bust cache so fetchPosts always re-fetches
      await fetchPosts(orgId, true);
    } finally {
      setSyncing(false);
    }
  };

  // ── Post creation ───────────────────────────────────────────────────────────
  const handlePublishPost = async (e) => {
    e.preventDefault();
    if (!postData.caption || !postData.imageUrl) {
      alert('Please fill in caption and image URL');
      return;
    }
    try {
      setPosting(true);
      const userid = getStoredUserId();
      const res = await axios.post(`${API_BASE_URL}/linkedin/post`, {
        caption: postData.caption,
        image_url: postData.imageUrl,
        organization_id: selectedOrg.organization_id
      }, { headers: { userid } });

      if (res.data.success) {
        setShowPostModal(false);
        setPostData({ caption: '', imageUrl: '' });
        await fetchPosts(selectedOrg.organization_id);
      }
    } catch (err) {
      alert(`Failed to publish: ${err.response?.data?.message || err.message}`);
    } finally {
      setPosting(false);
    }
  };

  // ── Disconnect ──────────────────────────────────────────────────────────────
  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect your LinkedIn account?')) return;
    try {
      setLoading(true);
      const userid = getStoredUserId();
      await axios.post(`${API_BASE_URL}/linkedin/auth/disconnect`, {}, { headers: { userid } });
      setOrganizations([]);
      setSelectedOrg(null);
      setAnalytics(null);
      setPosts([]);
      setConnectionStatus({ connected: false });
    } catch (err) {
      alert('Failed to disconnect: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // ── Normalise demographics for pie charts ───────────────────────────────────
  const normDemographics = (arr, labelKey, countPath) => {
    if (!Array.isArray(arr)) return [];
    return arr
      .map(item => ({
        name: String(item[labelKey] || '').split(':').pop() || 'Unknown',
        value: item.followerCounts?.organicFollowerCount || 0
      }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value);
  };

  // ── Early returns ───────────────────────────────────────────────────────────
  if (loading) {
    return <Spinner label="Loading LinkedIn data..." />;
  }

  if (!connectionStatus?.connected) {
    return (
      <div className="li-empty-state">
        <div className="li-empty-icon">🔗</div>
        <p>LinkedIn account not connected</p>
        <p className="li-hint">Connect your LinkedIn account to view analytics and manage posts</p>
      </div>
    );
  }

  if (organizations.length === 0) {
    return (
      <div className="li-empty-state">
        <div className="li-empty-icon">🏢</div>
        <p>No LinkedIn organizations found</p>
        <p className="li-hint">You need admin access to at least one LinkedIn organization page</p>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="li-analytics">

      {/* ── Reconnect banner (shown when token was revoked) ─────────────── */}
      {requiresReconnect && (
        <div className="li-reconnect-banner">
          <span className="li-reconnect-icon">⚠️</span>
          <span className="li-reconnect-msg">
            Your LinkedIn session has expired or was revoked. Please reconnect to resume syncing.
          </span>
          <button
            className="li-reconnect-btn"
            onClick={() => window.location.href = '/social-media-automation'}
          >
            Reconnect LinkedIn
          </button>
        </div>
      )}

      {/* ── Post Modal ─────────────────────────────────────────────────────── */}
      {showPostModal && (
        <div className="li-modal-overlay" onClick={() => setShowPostModal(false)}>
          <div className="li-modal" onClick={e => e.stopPropagation()}>
            <div className="li-modal-header">
              <h3>Create LinkedIn Post</h3>
              <button className="li-modal-close" onClick={() => setShowPostModal(false)}>×</button>
            </div>
            <form onSubmit={handlePublishPost} className="li-modal-body">
              <div className="li-form-group">
                <label>Caption</label>
                <textarea
                  value={postData.caption}
                  onChange={e => setPostData({ ...postData, caption: e.target.value })}
                  placeholder="What do you want to share?"
                  rows={5}
                />
              </div>
              <div className="li-form-group">
                <label>Image URL</label>
                <input
                  type="url"
                  value={postData.imageUrl}
                  onChange={e => setPostData({ ...postData, imageUrl: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                />
                {postData.imageUrl && (
                  <div className="li-img-preview">
                    <img src={postData.imageUrl} alt="Preview" />
                  </div>
                )}
              </div>
              <div className="li-form-actions">
                <button type="button" className="li-btn-secondary" onClick={() => setShowPostModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="li-btn-primary" disabled={posting}>
                  {posting ? 'Publishing…' : 'Publish Post'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Page Header ────────────────────────────────────────────────────── */}
      <div className="li-page-header">
        <div className="li-page-header-top">
          <div className="li-page-title">
            <span className="li-logo">in</span>
            <h2>LinkedIn Analytics</h2>
          </div>
          <div className="li-header-actions">
            <select
              className="li-day-select"
              value={dayRange}
              onChange={e => setDayRange(Number(e.target.value))}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              className="li-action-btn li-btn-outline"
              onClick={() => selectedOrg && syncPosts(selectedOrg.organization_id)}
              disabled={syncing || !selectedOrg}
            >
              {syncing ? '⟳ Syncing…' : '⟳ Sync Posts'}
            </button>
            <button
              className="li-action-btn li-btn-primary"
              onClick={() => setShowPostModal(true)}
              disabled={!selectedOrg}
            >
              + New Post
            </button>
            <button
              className="li-action-btn li-btn-danger"
              onClick={handleDisconnect}
            >
              Disconnect
            </button>
          </div>
        </div>

        {/* Org selector */}
        <div className="li-org-bar">
          {organizations.map(org => (
            <button
              key={org.organization_id}
              className={`li-org-tab ${selectedOrg?.organization_id === org.organization_id ? 'active' : ''}`}
              onClick={() => setSelectedOrg(org)}
            >
              {org.logo_url && (
                <img src={org.logo_url} alt="" className="li-org-logo"
                  onError={e => { e.target.style.display = 'none'; }} />
              )}
              <span className="li-org-name">{org.organization_name}</span>
              {org.is_primary && <span className="li-primary-badge">Primary</span>}
              {org.follower_count > 0 && (
                <span className="li-org-followers">{fmtNum(org.follower_count)} followers</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab bar */}
        <div className="li-tab-bar">
          {[
            { id: 'overview', label: '📊 Overview' },
            { id: 'followers', label: '👥 Followers' },
            { id: 'pageviews', label: '📄 Page Views' },
            { id: 'posts', label: '📝 Posts' }
          ].map(tab => (
            <button
              key={tab.id}
              className={`li-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab Content ────────────────────────────────────────────────────── */}
      <div className="li-tab-content">

        {/* ── OVERVIEW TAB ─────────────────────────────────────────────────── */}
        {activeTab === 'overview' && (
          <div className="li-tab-pane">
            {analyticsLoading ? <Spinner label="Loading analytics…" /> : (
              <>
                {/* KPI cards */}
                <div className="li-metrics-grid">
                  <MetricCard
                    icon="👥" label="Followers"
                    value={fmtNum(analytics?.current_followers || selectedOrg?.follower_count || 0)}
                    sub={analytics?.net_follower_growth != null
                      ? `${analytics.net_follower_growth >= 0 ? '+' : ''}${fmtNum(analytics.net_follower_growth)} this period`
                      : null}
                    subPositive={analytics?.net_follower_growth >= 0}
                  />
                  <MetricCard
                    icon="👁️" label="Impressions"
                    value={fmtNum(analytics?.total_impressions || 0)}
                  />
                  <MetricCard
                    icon="🔗" label="Clicks"
                    value={fmtNum(analytics?.total_clicks || 0)}
                  />
                  <MetricCard
                    icon="❤️" label="Likes"
                    value={fmtNum(analytics?.total_likes || 0)}
                  />
                  <MetricCard
                    icon="💬" label="Comments"
                    value={fmtNum(analytics?.total_comments || 0)}
                  />
                  <MetricCard
                    icon="↗️" label="Shares"
                    value={fmtNum(analytics?.total_shares || 0)}
                  />
                  <MetricCard
                    icon="📡" label="Reach"
                    value={fmtNum(analytics?.total_reach || 0)}
                  />
                  <MetricCard
                    icon="📈" label="Avg Engagement Rate"
                    value={`${(analytics?.avg_engagement_rate || 0).toFixed(2)}%`}
                  />
                </div>

                {/* Engagement timeline chart */}
                <SectionCard title="Engagement Over Time">
                  {engagementTimeline.length === 0 ? <EmptyChart /> : (
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={engagementTimeline} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gradImpressions" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#0a66c2" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#0a66c2" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gradClicks" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#43a047" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#43a047" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend />
                        <Area type="monotone" dataKey="Impressions" stroke="#0a66c2" fill="url(#gradImpressions)" strokeWidth={2} />
                        <Area type="monotone" dataKey="Clicks" stroke="#43a047" fill="url(#gradClicks)" strokeWidth={2} />
                        <Area type="monotone" dataKey="Likes" stroke="#fb8c00" fill="none" strokeWidth={2} />
                        <Area type="monotone" dataKey="Comments" stroke="#8e24aa" fill="none" strokeWidth={2} />
                        <Area type="monotone" dataKey="Shares" stroke="#e53935" fill="none" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </SectionCard>

                {/* Recent posts preview */}
                <SectionCard title="Recent Posts" action={
                  <button className="li-link-btn" onClick={() => setActiveTab('posts')}>View all →</button>
                }>
                  {postsLoading ? <Spinner /> : posts.length === 0 ? (
                    <EmptyChart msg="No posts yet. Create your first post!" />
                  ) : (
                    <div className="li-posts-preview">
                      {posts.slice(0, 3).map(post => (
                        <div key={post.post_id} className="li-post-preview-card">
                          {post.media_url && !post.media_url.startsWith('urn:') && (
                            <img src={post.media_url} alt="" className="li-post-thumb"
                              onError={e => { e.target.style.display = 'none'; }} />
                          )}
                          <div className="li-post-preview-body">
                            <p className="li-post-preview-text">
                              {post.post_text
                                ? post.post_text.substring(0, 120) + (post.post_text.length > 120 ? '…' : '')
                                : <em>No caption</em>}
                            </p>
                            <div className="li-post-preview-stats">
                              <span>❤️ {fmtNum(post.likes || 0)}</span>
                              <span>💬 {fmtNum(post.comments || 0)}</span>
                              <span>↗️ {fmtNum(post.shares || 0)}</span>
                              {post.impressions > 0 && <span>👁️ {fmtNum(post.impressions)}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </SectionCard>
              </>
            )}
          </div>
        )}

        {/* ── FOLLOWERS TAB ────────────────────────────────────────────────── */}
        {activeTab === 'followers' && (
          <div className="li-tab-pane">
            {followersLoading ? <Spinner label="Loading follower data…" /> : (
              <>
                {/* Follower gain KPIs */}
                {followerGains && (
                  <div className="li-metrics-grid">
                    <MetricCard icon="📈" label="Total Follower Gain"
                      value={fmtNum(followerGains.summary?.total_gain || 0)}
                      sub={`${fmtNum(followerGains.summary?.total_organic_gain || 0)} organic`}
                      subPositive={true}
                    />
                    <MetricCard icon="🌱" label="Organic Gain"
                      value={fmtNum(followerGains.summary?.total_organic_gain || 0)}
                    />
                    <MetricCard icon="💰" label="Paid Gain"
                      value={fmtNum(followerGains.summary?.total_paid_gain || 0)}
                    />
                    <MetricCard icon="👥" label="Current Followers"
                      value={fmtNum(analytics?.current_followers || selectedOrg?.follower_count || 0)}
                    />
                  </div>
                )}

                {/* Follower gains chart */}
                <SectionCard title="Follower Gains Over Time">
                  {!followerGains?.data?.length ? <EmptyChart /> : (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={followerGains.data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="organicGain" name="Organic" fill="#0a66c2" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="paidGain" name="Paid" fill="#43a047" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </SectionCard>

                {/* Demographics */}
                {followerDemographics && (
                  <div className="li-demo-grid">
                    <SectionCard title="By Country">
                      <DemoPieChart
                        data={normDemographics(followerDemographics.byCountry, 'geo', 'value')}
                        nameKey="name" valueKey="value"
                      />
                    </SectionCard>
                    <SectionCard title="By Industry">
                      <DemoPieChart
                        data={normDemographics(followerDemographics.byIndustry, 'industry', 'value')}
                        nameKey="name" valueKey="value"
                      />
                    </SectionCard>
                    <SectionCard title="By Function">
                      <DemoPieChart
                        data={normDemographics(followerDemographics.byFunction, 'function', 'value')}
                        nameKey="name" valueKey="value"
                      />
                    </SectionCard>
                    <SectionCard title="By Seniority">
                      <DemoPieChart
                        data={normDemographics(followerDemographics.bySeniority, 'seniority', 'value')}
                        nameKey="name" valueKey="value"
                      />
                    </SectionCard>
                    <SectionCard title="By Company Size">
                      <DemoPieChart
                        data={normDemographics(followerDemographics.byStaffCount, 'staffCountRange', 'value')}
                        nameKey="name" valueKey="value"
                      />
                    </SectionCard>
                    <SectionCard title="By Association Type">
                      <DemoPieChart
                        data={normDemographics(followerDemographics.byAssociationType, 'associationType', 'value')}
                        nameKey="name" valueKey="value"
                      />
                    </SectionCard>
                  </div>
                )}
                {!followerDemographics && !followersLoading && (
                  <EmptyChart msg="Follower demographics not available. This requires rw_organization_admin scope." />
                )}
              </>
            )}
          </div>
        )}

        {/* ── PAGE VIEWS TAB ───────────────────────────────────────────────── */}
        {activeTab === 'pageviews' && (
          <div className="li-tab-pane">
            {pageLoading ? <Spinner label="Loading page view data…" /> : (
              <>
                {/* ── Permission denied ───────────────────────────────────── */}
                {pagePermissionDenied && (
                  <div className="li-reconnect-banner">
                    <span className="li-reconnect-icon">🔒</span>
                    <span className="li-reconnect-msg">
                      Page statistics require the <strong>rw_organization_admin</strong> permission.
                      Your current LinkedIn session was not granted this scope.
                      Please reconnect to enable page analytics.
                    </span>
                    <button
                      className="li-reconnect-btn"
                      onClick={() => window.location.href = '/social-media-automation'}
                    >
                      Reconnect LinkedIn
                    </button>
                  </div>
                )}

                {/* ── No data banner (network error, not scope) ─────────────── */}
                {!pageStats && !pagePermissionDenied && (
                  <div className="li-info-banner">
                    <span>⚠️</span>
                    <span>Page statistics could not be loaded. Click <strong>Refresh</strong> to try again.</span>
                    <button className="li-btn-outline li-action-btn"
                      onClick={() => selectedOrg && fetchPageAnalytics(selectedOrg.organization_id, dayRange)}>
                      ↺ Refresh
                    </button>
                  </div>
                )}

                {/* ── Total Page View KPIs ─────────────────────────────────── */}
                <CollapsibleSection
                  title="Total Lifetime Page Statistics"
                  defaultOpen={true}
                  badge={pageStats ? fmtNum(pageStats.totalPageStatistics?.views?.allPageViews?.pageViews || 0) + ' total views' : null}
                >
                  {!pageStats?.totalPageStatistics ? (
                    <EmptyChart msg={pagePermissionDenied ? 'Reconnect LinkedIn with rw_organization_admin scope to view page statistics.' : 'No page statistics data available.'} />
                  ) : (() => {
                    const v = pageStats.totalPageStatistics.views || {};
                    const c = pageStats.totalPageStatistics.clicks || {};
                    const desktopBtns = c.desktopCustomButtonClickCounts || [];
                    const mobileBtns  = c.mobileCustomButtonClickCounts  || [];
                    const totalBtnClicks = [...desktopBtns, ...mobileBtns].reduce((sum, b) => sum + (b.clicks || 0), 0);
                    return (
                      <div className="li-metrics-grid">
                        <MetricCard icon="📄" label="All Page Views"
                          value={fmtNum(v.allPageViews?.pageViews || 0)}
                          sub="Desktop + Mobile combined"
                        />
                        <MetricCard icon="🖥️" label="All Desktop Views"
                          value={fmtNum(v.allDesktopPageViews?.pageViews || 0)}
                        />
                        <MetricCard icon="📱" label="All Mobile Views"
                          value={fmtNum(v.allMobilePageViews?.pageViews || 0)}
                        />
                        <MetricCard icon="🏠" label="Overview Page Views"
                          value={fmtNum(v.overviewPageViews?.pageViews || 0)}
                          sub={`🖥️ ${fmtNum(v.desktopOverviewPageViews?.pageViews || 0)}  📱 ${fmtNum(v.mobileOverviewPageViews?.pageViews || 0)}`}
                        />
                        <MetricCard icon="💼" label="Careers Page Views"
                          value={fmtNum(v.careersPageViews?.pageViews || 0)}
                          sub={`🖥️ ${fmtNum(v.desktopCareersPageViews?.pageViews || 0)}  📱 ${fmtNum(v.mobileCareersPageViews?.pageViews || 0)}`}
                        />
                        <MetricCard icon="👔" label="Jobs Page Views"
                          value={fmtNum(v.jobsPageViews?.pageViews || 0)}
                          sub={`🖥️ ${fmtNum(v.desktopJobsPageViews?.pageViews || 0)}  📱 ${fmtNum(v.mobileJobsPageViews?.pageViews || 0)}`}
                        />
                        <MetricCard icon="🌟" label="Life At Page Views"
                          value={fmtNum(v.lifeAtPageViews?.pageViews || 0)}
                          sub={`🖥️ ${fmtNum(v.desktopLifeAtPageViews?.pageViews || 0)}  📱 ${fmtNum(v.mobileLifeAtPageViews?.pageViews || 0)}`}
                        />
                        {totalBtnClicks > 0 && (
                          <MetricCard icon="🖱️" label="Custom Button Clicks"
                            value={fmtNum(totalBtnClicks)}
                            sub={`🖥️ ${fmtNum(desktopBtns.reduce((s, b) => s + (b.clicks || 0), 0))}  📱 ${fmtNum(mobileBtns.reduce((s, b) => s + (b.clicks || 0), 0))}`}
                          />
                        )}
                      </div>
                    );
                  })()}
                </CollapsibleSection>

                {/* ── Desktop vs Mobile by Section ─────────────────────────── */}
                <CollapsibleSection title="Desktop vs Mobile — Page Section Breakdown" defaultOpen={true}>
                  {!pageStats?.totalPageStatistics ? <EmptyChart /> : (() => {
                    const v = pageStats.totalPageStatistics.views || {};
                    const sections = [
                      { name: 'Overview', desktop: v.desktopOverviewPageViews?.pageViews || 0, mobile: v.mobileOverviewPageViews?.pageViews || 0 },
                      { name: 'Careers',  desktop: v.desktopCareersPageViews?.pageViews  || 0, mobile: v.mobileCareersPageViews?.pageViews  || 0 },
                      { name: 'Jobs',     desktop: v.desktopJobsPageViews?.pageViews     || 0, mobile: v.mobileJobsPageViews?.pageViews     || 0 },
                      { name: 'Life At',  desktop: v.desktopLifeAtPageViews?.pageViews   || 0, mobile: v.mobileLifeAtPageViews?.pageViews   || 0 },
                    ].filter(s => s.desktop + s.mobile > 0);
                    if (sections.length === 0) return <EmptyChart msg="No page section breakdown data available." />;
                    return (
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={sections} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(val) => fmtNum(val)} />
                          <Legend />
                          <Bar dataKey="desktop" name="Desktop" fill="#0a66c2" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="mobile"  name="Mobile"  fill="#00b0ff" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    );
                  })()}
                </CollapsibleSection>

                {/* ── Page Views Over Time ─────────────────────────────────── */}
                <CollapsibleSection title="Page Views Over Time" defaultOpen={true}>
                  {pageTimeline.length === 0 ? <EmptyChart msg="No time-bound page view data for this period." /> : (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={pageTimeline} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => fmtNum(v)} />
                        <Legend />
                        <Line type="monotone" dataKey="All Views" stroke="#0a66c2" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="Overview"  stroke="#43a047" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="Careers"   stroke="#fb8c00" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="Jobs"      stroke="#8e24aa" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="Life At"   stroke="#e53935" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="Mobile"    stroke="#00b0ff" strokeWidth={1} dot={false} strokeDasharray="4 2" />
                        <Line type="monotone" dataKey="Desktop"   stroke="#546e7a" strokeWidth={1} dot={false} strokeDasharray="4 2" />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CollapsibleSection>

                {/* ── Audience Segmentation ────────────────────────────────── */}
                <CollapsibleSection title="Audience Segmentation — Who's Viewing Your Page" defaultOpen={true}>
                  {!pageStats ? <EmptyChart msg="No segmentation data available yet." /> : (
                    <div className="li-demo-grid">
                      <SectionCard title="Views by Country">
                        <DemoPieChart
                          data={(pageStats.byCountry || []).map(d => ({
                            name: String(d.geo || '').split(':').pop() || 'Unknown',
                            value: d.pageStatistics?.views?.allPageViews?.pageViews || 0
                          })).filter(d => d.value > 0).sort((a, b) => b.value - a.value)}
                          nameKey="name" valueKey="value"
                        />
                      </SectionCard>

                      <SectionCard title="Views by Region">
                        <DemoPieChart
                          data={(pageStats.byRegion || []).map(d => ({
                            name: String(d.geo || '').split(':').pop() || 'Unknown',
                            value: d.pageStatistics?.views?.allPageViews?.pageViews || 0
                          })).filter(d => d.value > 0).sort((a, b) => b.value - a.value)}
                          nameKey="name" valueKey="value"
                        />
                      </SectionCard>

                      <SectionCard title="Views by Job Function">
                        <DemoPieChart
                          data={(pageStats.byFunction || []).map(d => ({
                            name: String(d.function || '').split(':').pop() || 'Unknown',
                            value: d.pageStatistics?.views?.allPageViews?.pageViews || 0
                          })).filter(d => d.value > 0).sort((a, b) => b.value - a.value)}
                          nameKey="name" valueKey="value"
                        />
                      </SectionCard>

                      <SectionCard title="Views by Industry">
                        <DemoPieChart
                          data={(pageStats.byIndustry || []).map(d => ({
                            name: String(d.industryV2 || d.industry || '').split(':').pop() || 'Unknown',
                            value: d.pageStatistics?.views?.allPageViews?.pageViews || 0
                          })).filter(d => d.value > 0).sort((a, b) => b.value - a.value)}
                          nameKey="name" valueKey="value"
                        />
                      </SectionCard>

                      <SectionCard title="Views by Seniority">
                        <DemoPieChart
                          data={(pageStats.bySeniority || []).map(d => ({
                            name: String(d.seniority || '').split(':').pop() || 'Unknown',
                            value: d.pageStatistics?.views?.allPageViews?.pageViews || 0
                          })).filter(d => d.value > 0).sort((a, b) => b.value - a.value)}
                          nameKey="name" valueKey="value"
                        />
                      </SectionCard>

                      <SectionCard title="Views by Company Size">
                        <DemoPieChart
                          data={(pageStats.byStaffCount || []).map(d => ({
                            name: String(d.staffCountRange || '').replace('SIZE_', '').replace(/_OR_MORE/, '+').replace(/_/g, '–'),
                            value: d.pageStatistics?.views?.allPageViews?.pageViews || 0
                          })).filter(d => d.value > 0).sort((a, b) => b.value - a.value)}
                          nameKey="name" valueKey="value"
                        />
                      </SectionCard>
                    </div>
                  )}
                </CollapsibleSection>

                {/* ── Top Countries bar chart ───────────────────────────────── */}
                {pageStats?.byCountry?.length > 0 && (() => {
                  const countryData = pageStats.byCountry
                    .map(d => ({
                      name: String(d.geo || '').split(':').pop() || 'Unknown',
                      Desktop: d.pageStatistics?.views?.allDesktopPageViews?.pageViews || 0,
                      Mobile:  d.pageStatistics?.views?.allMobilePageViews?.pageViews  || 0,
                      Total:   d.pageStatistics?.views?.allPageViews?.pageViews         || 0,
                    }))
                    .filter(d => d.Total > 0)
                    .sort((a, b) => b.Total - a.Total)
                    .slice(0, 10);
                  if (countryData.length === 0) return null;
                  return (
                    <CollapsibleSection title="Top Countries — Desktop vs Mobile" defaultOpen={false}>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={countryData} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(val) => fmtNum(val)} />
                          <Legend />
                          <Bar dataKey="Desktop" fill="#0a66c2" radius={[3, 3, 0, 0]} />
                          <Bar dataKey="Mobile"  fill="#00b0ff" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CollapsibleSection>
                  );
                })()}
              </>
            )}
          </div>
        )}

        {/* ── POSTS TAB ────────────────────────────────────────────────────── */}
        {activeTab === 'posts' && (
          <div className="li-tab-pane">
            {/* Toolbar */}
            <div className="li-posts-toolbar">
              <span className="li-posts-count">
                {postsLoading ? 'Loading…' : `${posts.length} post${posts.length !== 1 ? 's' : ''}`}
              </span>
              <div className="li-posts-actions">
                <button
                  className="li-action-btn li-btn-outline"
                  onClick={() => selectedOrg && syncPosts(selectedOrg.organization_id)}
                  disabled={syncing || postsLoading}
                >
                  {syncing ? '⟳ Syncing…' : '⟳ Sync with LinkedIn'}
                </button>
                <button
                  className="li-action-btn li-btn-primary"
                  onClick={() => setShowPostModal(true)}
                >
                  + New Post
                </button>
              </div>
            </div>

            {postsLoading ? <Spinner label="Loading posts from LinkedIn…" /> : posts.length === 0 ? (
              <div className="li-empty-state">
                <div className="li-empty-icon">📝</div>
                <p>No posts found</p>
                <p className="li-hint">Click "Sync with LinkedIn" to fetch your organization's posts, or create a new one.</p>
                <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                  <button className="li-action-btn li-btn-outline"
                    onClick={() => selectedOrg && syncPosts(selectedOrg.organization_id)}
                    disabled={syncing}>
                    {syncing ? '⟳ Syncing…' : '⟳ Sync Posts'}
                  </button>
                  <button className="li-action-btn li-btn-primary" onClick={() => setShowPostModal(true)}>
                    + Create Post
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* ── Performance chart (only if we have engagement data) ── */}
                {posts.some(p => (p.impressions || 0) + (p.likes || 0) + (p.clicks || 0) > 0) && (
                  <CollapsibleSection title="Post Performance Overview" defaultOpen={true}
                    badge={`${posts.length} posts`}>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart
                        data={[...posts]
                          .sort((a, b) => (b.impressions || 0) - (a.impressions || 0))
                          .slice(0, 10)
                          .map(p => ({
                            name: p.post_text ? p.post_text.substring(0, 22) + '…' : p.post_id?.substring(0, 12),
                            Impressions: p.impressions || 0,
                            Clicks: p.clicks || 0,
                            Likes: p.likes || 0,
                            Comments: p.comments || 0,
                            Shares: p.shares || 0
                          }))}
                        margin={{ top: 10, right: 20, left: 0, bottom: 70 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => fmtNum(v)} />
                        <Legend />
                        <Bar dataKey="Impressions" fill="#0a66c2" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="Clicks"      fill="#43a047" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="Likes"       fill="#fb8c00" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="Comments"    fill="#8e24aa" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="Shares"      fill="#e53935" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CollapsibleSection>
                )}

                {/* ── Posts grid ─────────────────────────────────────────── */}
                <CollapsibleSection title="All Posts" defaultOpen={true} badge={`${posts.length}`}>
                  <div className="li-posts-grid">
                    {posts.map(post => {
                      const contentType = post.media_type || 'TEXT';
                      const isArticle = contentType === 'ARTICLE';
                      const isVideo = contentType === 'VIDEO';
                      const isImage = contentType === 'IMAGE' || contentType === 'MULTI_IMAGE';
                      const hasMedia = post.media_url && !post.media_url.startsWith('urn:');
                      const engRate = post.engagement_rate
                        ? (Number(post.engagement_rate) < 1
                            ? (Number(post.engagement_rate) * 100).toFixed(2)
                            : Number(post.engagement_rate).toFixed(2))
                        : null;

                      return (
                        <div key={post.post_id} className="li-post-card">
                          {/* Content type badge */}
                          <div className="li-post-type-badge">
                            {isVideo ? '🎬 Video' : isArticle ? '📰 Article' : isImage ? '🖼️ Image' : '📝 Text'}
                          </div>

                          {/* Media preview */}
                          {hasMedia && isImage && (
                            <div className="li-post-img-wrap">
                              <img src={post.media_url} alt="Post media"
                                onError={e => { e.target.parentElement.style.display = 'none'; }} />
                            </div>
                          )}
                          {isArticle && post.article_title && (
                            <div className="li-post-article-preview">
                              <span className="li-post-article-icon">📰</span>
                              <div>
                                <div className="li-post-article-title">{post.article_title}</div>
                                {post.article_source && (
                                  <div className="li-post-article-source">{post.article_source}</div>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="li-post-body">
                            {/* Post text */}
                            <p className="li-post-text">
                              {post.post_text
                                ? post.post_text.substring(0, 200) + (post.post_text.length > 200 ? '…' : '')
                                : <em className="li-no-caption">No caption</em>}
                            </p>

                            {/* Engagement stats */}
                            <div className="li-post-stats-row">
                              <span title="Likes">❤️ {fmtNum(post.likes || 0)}</span>
                              <span title="Comments">💬 {fmtNum(post.comments || 0)}</span>
                              <span title="Shares">↗️ {fmtNum(post.shares || 0)}</span>
                              {(post.impressions || 0) > 0 && (
                                <span title="Impressions">👁️ {fmtNum(post.impressions)}</span>
                              )}
                              {(post.clicks || 0) > 0 && (
                                <span title="Clicks">🔗 {fmtNum(post.clicks)}</span>
                              )}
                              {(post.reach || 0) > 0 && (
                                <span title="Reach">📡 {fmtNum(post.reach)}</span>
                              )}
                            </div>

                            {/* Engagement rate */}
                            {engRate && (
                              <div className="li-post-engagement">
                                {engRate}% engagement rate
                              </div>
                            )}

                            {/* Footer */}
                            <div className="li-post-footer">
                              <span className="li-post-date">
                                {post.posted_at
                                  ? new Date(post.posted_at).toLocaleDateString('en-US', {
                                      year: 'numeric', month: 'short', day: 'numeric'
                                    })
                                  : 'Recently'}
                              </span>
                              {post.post_url && (
                                <a href={post.post_url} target="_blank" rel="noopener noreferrer"
                                  className="li-post-link">
                                  View ↗
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CollapsibleSection>
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default LinkedInAnalytics;
